import React, { useMemo } from 'react';
import { UserSelect } from '@kineticdata/react';
import {
  Status,
  SuggestionsContainer,
  generateInput,
  generateMultipleSelectionsContainer,
  generateSingleSelectionsContainer,
  generateMultipleSelection,
  generateSingleSelection,
} from './TypeaheadDefaults';
import { FieldWrapper } from './FieldWrapper';
import classNames from 'classnames';
import { parseRenderOptions } from './utils';

const selectionToValue = selection =>
  selection.get('username')
    ? `${selection.get('displayName') ||
        selection.get('email') ||
        ''} <${selection.get('username')}>`
    : `New User <${selection.get('email')}>`;

const Suggestion = ({ suggestion, active, custom }) => (
  <div className={classNames('suggestion', { active })}>
    {custom ? (
      <>
        <div className="small">New User</div>
        <div className="large">{suggestion.get('email')}</div>
      </>
    ) : (
      <>
        <div className="large">{suggestion.get('displayName')}</div>
        <div className="small">{suggestion.get('username')}</div>
      </>
    )}
  </div>
);

const UserComponent = ({ multiple, renderOptions, components, ...props }) => (
  <FieldWrapper {...props} renderOptions={renderOptions} fieldset={multiple}>
    <UserSelect
      components={{ Status, SuggestionsContainer, Suggestion, ...components }}
      disabled={!props.enabled}
      minSearchLength={1}
      multiple={multiple}
      id={props.id}
      value={props.value}
      onChange={props.onChange}
      onBlur={props.onBlur}
      onFocus={props.onFocus}
      placeholder={renderOptions.placeholder}
      form={props.form}
      invalid={renderOptions.hasErrors}
    />
  </FieldWrapper>
);

/******************************************************************************
 * Single Field
 ******************************************************************************/

export const UserField = props => {
  const renderOptions = parseRenderOptions(props);
  const components = useMemo(
    () => ({
      Input: generateInput({
        size: renderOptions.size,
        className: renderOptions.fieldClassName,
      }),
      Selection: generateSingleSelection(selectionToValue, {
        size: renderOptions.size,
      }),
      SelectionsContainer: generateSingleSelectionsContainer({
        size: renderOptions.size,
      }),
    }),
    [renderOptions.size, renderOptions.fieldClassName],
  );
  return (
    <UserComponent
      renderOptions={renderOptions}
      components={components}
      {...props}
    />
  );
};

export const UserFilter = props => {
  const renderOptions = parseRenderOptions(props, null, {
    floating: true,
    size: 'sm',
  });
  const components = useMemo(
    () => ({
      Input: generateInput({
        size: renderOptions.size,
        className: renderOptions.fieldClassName,
      }),
      Selection: generateSingleSelection(selectionToValue, {
        size: renderOptions.size,
      }),
      SelectionsContainer: generateSingleSelectionsContainer({
        size: renderOptions.size,
      }),
    }),
    [renderOptions.size, renderOptions.fieldClassName],
  );
  return (
    <UserComponent
      renderOptions={renderOptions}
      components={components}
      {...props}
    />
  );
};

/******************************************************************************
 * Multi Field
 ******************************************************************************/

export const UserMultiField = props => {
  const renderOptions = parseRenderOptions(props, { isFloating: false });
  const components = useMemo(
    () => ({
      Input: generateInput({
        size: renderOptions.size,
        className: renderOptions.fieldClassName,
      }),
      Selection: generateMultipleSelection(selectionToValue, {
        size: renderOptions.size,
      }),
      SelectionsContainer: generateMultipleSelectionsContainer({
        size: renderOptions.size,
      }),
    }),
    [renderOptions.size, renderOptions.fieldClassName],
  );
  return (
    <UserComponent
      multiple={true}
      renderOptions={renderOptions}
      components={components}
      {...props}
    />
  );
};
